import dominantEnergyMeanings from '../data/dominantEnergyMeanings';
import movingLineMeanings from '../data/movingLineMeanings';
import { getPhilosophyContent } from '../data/thamPhaiHieu.js';

const getMovingLineMeaning = (hexagramName, lineNumber) => {
  if (!movingLineMeanings[hexagramName] || !movingLineMeanings[hexagramName][lineNumber]) {
    return 'Thông tin chuyên sâu về hào động đang được cập nhật';
  }
  return movingLineMeanings[hexagramName][lineNumber];
};

const formatPhilosophyContent = (philosophy) => {
  if (!philosophy) return '';
  
  const { mainPillars } = philosophy;
  
  const formatKeyPoints = (points) => {
    return points.map(point => {
      let content = `#### ${point.title}\n`;
      
      if (point.aspects) {
        Object.entries(point.aspects).forEach(([key, value]) => {
          if (value.personalityTraits) {
            content += `- **Khía cạnh cá nhân:**\n${value.personalityTraits.map(trait => `  • ${trait}`).join('\n')}\n`;
          }
          if (value.coreValues) {
            content += `- **Giá trị cốt lõi:**\n${value.coreValues.map(val => `  • ${val}`).join('\n')}\n`;
          }
        });
      }
      
      if (point.methods) {
        if (Array.isArray(point.methods)) {
          content += `- **Phương pháp:**\n${point.methods.map(method => `  • ${method}`).join('\n')}\n`;
        } else if (point.methods.approaches) {
          content += `- **Cách tiếp cận:**\n${point.methods.approaches.map(approach => `  • ${approach}`).join('\n')}\n`;
        }
      }
      
      if (point.humanNature) {
        content += `- **Bản chất con người:**\n`;
        content += `  • ${point.humanNature.emotions}\n`;
        content += `  • ${point.humanNature.inherentNature}\n`;
      }
      
      return content;
    }).join('\n');
  };

  let content = `### ${philosophy.title}\n\n`;
  
  // Format Tham Phải Hiểu section
  content += `### ${mainPillars.thamPhaiHieu.title}\n`;
  content += formatKeyPoints(mainPillars.thamPhaiHieu.keyPoints);
  
  // Format Chọn Đúng Người section
  content += `\n### ${mainPillars.chonDungNguoi.title}\n`;
  mainPillars.chonDungNguoi.keyChoices.forEach(choice => {
    content += `#### ${choice.title}\n`;
    content += `- ${choice.description}\n`;
    if (choice.importance) content += `- Tầm quan trọng: ${choice.importance}\n`;
    if (choice.details) {
      content += `- Chi tiết:\n`;
      Object.entries(choice.details).forEach(([key, value]) => {
        if (typeof value === 'string') {
          content += `  • ${value}\n`;
        }
      });
    }
  });

  // Format Sống Như Ý section
  content += `\n### ${mainPillars.songNhuY.title}\n`;
  content += `Nguyên tắc: ${mainPillars.songNhuY.principles.join(', ')}\n`;

  // Format Giới-Định-Tuệ section
  content += `\n### ${mainPillars.gioiDinhTue.title}\n`;
  Object.entries(mainPillars.gioiDinhTue.elements).forEach(([key, element]) => {
    content += `#### ${element.title}\n`;
    content += `- ${element.description}\n`;
    if (element.principles) {
      content += `- Nguyên tắc:\n${element.principles.map(p => `  • ${p}`).join('\n')}\n`;
    }
    if (element.benefits) {
      content += `- Lợi ích: ${element.benefits}\n`;
    }
    if (element.relationship) {
      content += `- Mối quan hệ:\n${element.relationship.map(r => `  • ${r}`).join('\n')}\n`;
    }
  });

  return content;
};

export const generatePrompt = (hexagramData, gender, ageRange, maritalStatus, concern, job, businessContext, interactionGoal, dominantEnergy, combinedPairs, notificationList, ghiChuList, warningNotice) => {
  const dominantEnergyMeaning = dominantEnergy
    ? `Năng lượng chủ đạo **${dominantEnergy}** chi phối toàn bộ dãy số: 
       """${dominantEnergyMeanings[dominantEnergy]?.description || 'Đang hoàn thiện dữ liệu chuyên sâu'}"""`
    : 'Không xác định được năng lượng chủ đạo';

  const formatNotifications = (notifications) => {
    if (!notifications?.length) return '';
    return notifications.map(item => 
      `▸ **${item.code}** (${item.description}): ${item.meaning?.replace(/\n\s*-/g, ' ').replace(/\n+/g, ' ') || 'Phân tích chi tiết đang được cập nhật'}`
    ).join('\n\n');
  };

  const formatGhiChu = (warnings) => {
    if (!warnings?.length) return '';
    return warnings.map(item => 
      item.title.includes('nhiều 0') 
        ? `**${item.title}**: ${item.description} - Cảnh báo đặc biệt cần xem xét kỹ lưỡng`
        : `**${item.title}**: ${item.description}`
    ).join('. ');
  };

  const formatCombinedPairs = (pairs) => {
    if (!pairs?.length) return '';
    return pairs.map(pair => {
      const meaning = pair.meaning?.split('\n').map(line => 
        line.trim().replace(/^-/, '').replace(/:/, ': ')
      ).join(' ') || 'Ý nghĩa chuyên sâu đang được hoàn thiện';
      return `**${pair.number}** (${pair.energy}): ${meaning}`;
    }).join('\n\n');
  };

  const formatNumberPairs = (pairs) => {
    if (!pairs?.length) return 'Không phát hiện tổ hợp đặc biệt';
    return pairs.map(pair => 
      `▸ **${pair.pair}** (quẻ ${pair.hexagram}): ${pair.hexagramMeaning}`
    ).join('\n');
  };

  const formatEnergyDetails = (pairs) => {
    if (!pairs?.length) return 'Không xác định được dòng chảy năng lượng';
    
    const items = pairs.map(p => `${p.number} (${p.energy})`);
    if (items.length === 1) return `Bắt đầu và kết thúc với **${items[0]}**`;
    
    return items.slice(0, -1).join(', ') + ` và cuối cùng là **${items[items.length-1]}**`;
  };

  let additionalNotices = [];
  if (notificationList?.length) {
    additionalNotices.push(`**Tổ hợp cảnh báo**:\n${formatNotifications(notificationList)}`);
  }
  if (ghiChuList?.length) {
    additionalNotices.push(`**Ghi chú đặc biệt**: ${formatGhiChu(ghiChuList)}`);
  }
  if (warningNotice) {
    additionalNotices.push(`**Cảnh báo số đuôi**: ${warningNotice.title} - ${warningNotice.subtitle}\n${warningNotice.points.map(p => `▸ ${p}`).join('\n')}`);
  }

  const movingLineMeaning = getMovingLineMeaning(hexagramData.main.name, hexagramData.movingLine);

  const philosophyContent = getPhilosophyContent();
  const formattedPhilosophy = formatPhilosophyContent(philosophyContent);
  const philosophySection = formattedPhilosophy ? `
### Triết lý "Tham Phải Hiểu" áp dụng cho trường hợp này

${formattedPhilosophy}` : '';

  return `Phân tích số ${hexagramData.phoneNumber} theo Quẻ Kinh Dịch và Năng lượng số:
<input>  
Thông tin khách hàng:
- **SĐT**: ${hexagramData.phoneNumber}
- **Nhân khẩu học**: ${gender}, ${ageRange}, ${maritalStatus}
- **Công việc chính**: ${job || 'Chưa xác định'}
- **Vấn đề quan tâm**: **[${concern}]**
Thông tin quẻ Kinh Dịch:
- **Quẻ chính**: ${hexagramData.main.name} 
  → *${hexagramData.main.meaning}*
- **Hào động ${hexagramData.movingLine}**: 
  → *${movingLineMeaning}*
- **Quẻ biến**: ${hexagramData.transformed.name} 
  → *${hexagramData.transformed.meaning}*
- **Các quẻ Dịch then chốt**:
${formatNumberPairs(hexagramData.numberPairs)}
Thông tin năng lượng số:
- **Chuỗi tổ hợp năng lượng số**:
${formatEnergyDetails(combinedPairs)}
- **Năng lượng chủ đạo**:
${dominantEnergyMeaning}
- **Phân tích từng tổ hợp năng lượng số**:
${formatCombinedPairs(combinedPairs)}
- **Cảnh báo đặc biệt**:
${additionalNotices.join('\n\n') || 'Không có cảnh báo đặc biệt'}
</input>

Từ thông tin đã cung cấp ở <input>, hãy luận giải chi tiết, không trả lời chung chung về vấn đề **[${concern}]** của khách hàng. Hãy xem xét và đánh giá sâu sắc tác động của từng quẻ (như quẻ chính, hào động, quẻ biến, các quẻ chủ chốt, …) và từng loại năng lượng số (năng lượng chủ đạo, tổ hợp năng lượng số) vào các quẻ đó. Hãy chỉ rõ từng quẻ Dịch và tổ hợp số ảnh hưởng ra sao đối với vấn đề **[${concern}]** của người được xem, và giải thích các yếu tố bổ trợ và xung khắc liên quan đến các quẻ Dịch và tổ hợp năng lượng đó.

Hướng dẫn phân tích:
1. Phân tích tổng quan:
- Giải thích lý do đằng sau mỗi diễn giải để thể hiện chuyên môn.
2. Đề xuất giải pháp:
- Áp dụng triết lý "Tham phải hiểu" để đưa ra các giải pháp cụ thể, khả thi dựa trên vấn đề **[${concern}]** của khách hàng:
${philosophySection}
- Giải thích lợi ích của từng giải pháp đối với vấn đề **[${concern}]**.
- Liên hệ giải pháp với thông tin cá nhân của khách hàng.
3. Kết luận và lời khuyên:
- Tóm tắt các điểm chính của phân tích.
- Đưa ra lời khuyên cuối cùng tập trung vào vấn đề **[${concern}]**.
- Khuyến khích khách hàng hành động và thể hiện sự tin tưởng vào khả năng của họ.
Lưu ý:
- Trích dẫn số điện thoại ${hexagramData.phoneNumber} khi phân tích.
- Phải áp dụng triết lý "Tham phải hiểu"
- Đọc kỹ và hiểu rõ thông tin đầu vào, đặc biệt tập trung vào **${concern}**.
- Điều chỉnh giọng điệu phân tích sẽ thay đổi theo ý nghĩa các quẻ, tổ hợp số năng lượng số, năng lượng chủ đạo:
  - GIỌNG ĐIỆU TÍCH CỰC, NĂNG LƯỢNG: khi các quẻ, tổ hợp số năng lượng số, năng lượng chủ đạo BỔ TRỢ TỐT cho ${concern}. Sử dụng ngôn ngữ khích lệ, lạc quan, nhấn mạnh cơ hội, tiềm năng, điểm mạnh.
  - GIỌNG ĐIỆU ĐỒNG CẢM, THẤU HIỂU VÀ CẢNH BÁO (khi cần thiết): khi các quẻ, tổ hợp số năng lượng số, năng lượng chủ đạo KHÔNG BỔ TRỢ, tạo ra MÂU THUẪN, ĐIỂM NGHẼN, KHÓ KHĂN, BẾ TẮC cho ${concern}. Sử dụng ngôn ngữ thấu hiểu, chia sẻ, phân tích sâu vấn đề, cảnh báo về hệ quả, khuyến khích hành động thay đổi.
- Kết hợp phân tích Quẻ Kinh Dịch và Năng lượng số, tìm kiếm sự tương đồng, bổ sung hoặc mâu thuẫn và tác động đến **${concern}**. GHI CHÚ lại những điểm giao thoa và tác động chính liên quan đến **${concern}**.
- Xưng hô "anh" hoặc "chị" phù hợp.
- Trích dẫn tên quẻ và tổ hợp số để làm rõ luận điểm, ưu tiên sự mạch lạc và dễ hiểu.
- Tổng độ dài của phân tích cuối cùng nên nằm trong khoảng 800-1500 từ.
`;
};